import apiClient from '../../api/apiClient';

export interface AIChatbot {
  id: number;
  name: string;
  description: string;
  model: string;
  status: string;
  is_active: boolean;
  public_page_enabled?: boolean;
  initial_message: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains: string[];
  general_learning_set: string;
  created_at: string;
  updated_at: string;
  company_id: number;
  company_name?: string | null;
  widget_key: string;
  widget_settings?: any;
  settings?: any;
  training_status?: string;
  chat_mode?: string;
  // API key flags (keys themselves are never sent from backend for security)
  openai_api_key_set?: boolean;
  claude_api_key_set?: boolean;
  flows?: any[];
  modules?: ChatbotModule[];
}

export interface ModuleCatalogItem {
  id?: number;
  module_key: string;
  name: string;
  description?: string;
  is_enabled_globally: boolean;
  ui_order: number;
}

export interface ChatbotModule {
  module_key: string;
  status: string;
  enabled_by?: number | null;
  enabled_at?: string | null;
  disabled_at?: string | null;
  config_json?: Record<string, any> | null;
}

// Eksik interface'leri ekle
export interface AIChatbotCreate {
  name: string;
  description?: string;
  model: string;
  status: string;
  is_active: boolean;
  public_page_enabled?: boolean;
  initial_message?: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains?: string[];
  general_learning_set?: string;
  company_id: number;
  widget_settings?: any;
}

export interface AIChatbotUpdate {
  name?: string;
  description?: string;
  model?: string;
  status?: string;
  is_active?: boolean;
  public_page_enabled?: boolean;
  initial_message?: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains?: string[];
  general_learning_set?: string;
  company_id?: number;
  widget_settings?: any;
  settings?: any;
  training_status?: string;
  chat_mode?: string;
  flows?: any[];
}

export interface AIChatbotFilters {
  company_id?: number;
  status?: string;
  is_active?: boolean;
  search?: string;
  module?: string;
}

export interface TokenUsage {
  id: number;
  chatbot_id: number;
  session_id: string;
  model_name: string;
  operation_type: string;
  token_count: number;
  created_at: string;
}

export interface PersonalChatResponse {
  message: string;
  route?: string;
  is_flow_active?: boolean;
  session_id: string;
}

export interface PersonalChatHistoryResponse {
  session_id: string;
  has_more: boolean;
  messages: Array<{
    role: "user" | "assistant" | "system";
    content: string;
    created_at?: string | null;
  }>;
}

const getChatbots = async (params: any): Promise<any> => {
  // Backend skip/limit kullanıyor; frontend page/limit gönderiyor → dönüştür
  const { page, limit, ...rest } = params || {};
  const apiParams: any = { ...rest };
  if (limit != null) apiParams.limit = limit;
  if (page != null && limit != null) {
    apiParams.skip = (page - 1) * limit;
  }
  const data = await apiClient.get('/ai-chatbots', { params: apiParams });
  // API yanıtı bir dizi ise, istenilen formata dönüştür
  if (Array.isArray(data)) {
    return {
      chatbots: data,
      total: data.length,
    };
  }
  return data;
};

// getChatbot ve getChatbotById aynı şeyi yapıyor, birini diğerine yönlendir
const getChatbot = async (id: number): Promise<AIChatbot> => {
  const data = await apiClient.get(`/ai-chatbots/${id}`);
  return data;
};

const getChatbotById = async (id: number): Promise<AIChatbot> => {
  return getChatbot(id);
};

const createChatbot = async (data: AIChatbotCreate): Promise<AIChatbot> => {
  const res = await apiClient.post('/ai-chatbots', data);
  return res;
};

// updateChatbot fonksiyonunu düzelt, iki parametre alacak şekilde güncelle
const updateChatbot = async (id: number, data: AIChatbotUpdate): Promise<AIChatbot> => {
  const res = await apiClient.put(`/ai-chatbots/${id}`, data);
  return res;
};

const deleteChatbot = async (id: number): Promise<any> => {
  const res = await apiClient.delete(`/ai-chatbots/${id}`);
  return res;
};

export interface AIChatbotCopyRequest {
  target_company_id: number;
  name?: string;
}

const copyChatbot = async (chatbotId: number, body: AIChatbotCopyRequest): Promise<AIChatbot> => {
  const res = await apiClient.post(`/ai-chatbots/${chatbotId}/copy`, body);
  return res;
};

const getTokenUsage = async (params: any): Promise<TokenUsage[]> => {
  const data = await apiClient.get(`/ai-chatbots/${params.id}/token-usage`, {
    params: { skip: params.skip, limit: params.limit },
  });
  return data;
};

const getWidgetScript = (chatbot: AIChatbot): string => {
  return `<script>
  (function(w,d,s,o,f,js,fjs){
    w['AI-Chatbot-Widget']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
    w[o].l=1*new Date();js=d.createElement(s);fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','aiChat','https://chat-widget.example.com/loader.js'));
  aiChat('init', { 
    widgetKey: '${chatbot.widget_key}',
    chatbotName: '${chatbot.name}'
  });
</script>`;
};

const sendMessage = async (chatbotId: number, message: string) => {
  try {
    const data = await apiClient.post(`/ai-chatbots/${chatbotId}/test`, { message });
    return data;
  } catch (error) {
    console.error('Error sending message to chatbot:', error);
    throw error;
  }
};

const getConversationsList = async (params: any): Promise<any> => {
  const data = await apiClient.get('/ai-chatbots/conversations/list', { params });
  return data;
};

const getChatbotConversations = async (chatbotId: number, params: any): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/conversations`, { params });
  return data;
};

const getConversationMessages = async (conversationId: number, params: any): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${conversationId}/messages`, { params });
  return data;
};

const getModulesCatalog = async (): Promise<ModuleCatalogItem[]> => {
  return await apiClient.get('/ai-chatbots/modules/catalog');
};

const getChatbotModules = async (chatbotId: number): Promise<ChatbotModule[]> => {
  return await apiClient.get(`/ai-chatbots/${chatbotId}/modules`);
};

const updateChatbotModules = async (
  chatbotId: number,
  modules: Array<{ module_key: string; enabled: boolean; config_json?: Record<string, any> }>
): Promise<ChatbotModule[]> => {
  return await apiClient.put(`/ai-chatbots/${chatbotId}/modules`, { modules });
};

export interface ModulesOverviewRow {
  chatbot_id: number;
  chatbot_name: string;
  company_id: number;
  company_name?: string | null;
  modules: Array<{ module_key: string; status: string; enabled_at?: string | null }>;
}

const getModulesOverview = async (params?: {
  skip?: number; limit?: number; search?: string; module?: string; company_id?: number; has_modules?: boolean;
}): Promise<{ data: ModulesOverviewRow[]; total: number; skip: number; limit: number }> => {
  return await apiClient.get('/ai-chatbots/modules/overview', { params });
};

const createModuleCatalogItem = async (payload: {
  module_key: string; name: string; description?: string; is_enabled_globally?: boolean; ui_order?: number;
}): Promise<ModuleCatalogItem> => {
  return await apiClient.post('/ai-chatbots/modules/catalog', payload);
};

const updateModuleCatalogItem = async (
  moduleKey: string, payload: { name?: string; description?: string; is_enabled_globally?: boolean; ui_order?: number; }
): Promise<ModuleCatalogItem> => {
  return await apiClient.put(`/ai-chatbots/modules/catalog/${moduleKey}`, payload);
};

const deleteModuleCatalogItem = async (moduleKey: string): Promise<any> => {
  return await apiClient.delete(`/ai-chatbots/modules/catalog/${moduleKey}`);
};

const sendPersonalChatMessage = async (chatbotId: number, message: string): Promise<PersonalChatResponse> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/chat`, { message });
  return data;
};

const getPersonalChatHistory = async (
  chatbotId: number,
  params?: { skip?: number; limit?: number }
): Promise<PersonalChatHistoryResponse> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/chat/history`, { params });
  return data;
};

// ─── Conversation Tags & Annotations ───

export interface ConversationTag {
  id: number;
  name: string;
  color: string;
  company_id: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface ConversationAnnotationTag {
  id: number;
  name: string;
  color: string;
}

/** Single note in shared note history (chatbot-level, with author and date) */
export interface ConversationNoteItem {
  id: number;
  user_id: number;
  user_name: string;
  note_text: string;
  created_at: string;
}

export interface ConversationAnnotation {
  id?: number | null;
  conversation_id: number;
  user_id: number;
  is_favorite: boolean;
  notes: ConversationNoteItem[];
  tags: ConversationAnnotationTag[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AnnotationInline {
  id?: number | null;
  is_favorite: boolean;
  notes?: ConversationNoteItem[];
  tags: ConversationAnnotationTag[];
}

const getConversationTags = async (params: { company_id?: number; skip?: number; limit?: number }): Promise<any> => {
  const data = await apiClient.get('/ai-chatbots/conversations/tags', { params });
  return data;
};

const createConversationTag = async (body: { name: string; color: string; company_id: number }): Promise<ConversationTag> => {
  const data = await apiClient.post('/ai-chatbots/conversations/tags', body);
  return data;
};

const updateConversationTag = async (tagId: number, body: { name?: string; color?: string }): Promise<ConversationTag> => {
  const data = await apiClient.put(`/ai-chatbots/conversations/tags/${tagId}`, body);
  return data;
};

const deleteConversationTag = async (tagId: number): Promise<void> => {
  await apiClient.delete(`/ai-chatbots/conversations/tags/${tagId}`);
};

const getConversationAnnotation = async (conversationId: number): Promise<ConversationAnnotation> => {
  const data = await apiClient.get(`/ai-chatbots/conversations/${conversationId}/annotation`);
  return data;
};

const upsertConversationAnnotation = async (
  conversationId: number,
  body: { note?: string | null; is_favorite?: boolean; tag_ids?: number[] }
): Promise<ConversationAnnotation> => {
  const data = await apiClient.put(`/ai-chatbots/conversations/${conversationId}/annotation`, body);
  return data;
};

const deleteConversationAnnotation = async (conversationId: number): Promise<void> => {
  await apiClient.delete(`/ai-chatbots/conversations/${conversationId}/annotation`);
};

const toggleConversationFavorite = async (conversationId: number): Promise<ConversationAnnotation> => {
  const data = await apiClient.put(`/ai-chatbots/conversations/${conversationId}/favorite`);
  return data;
};

const aiChatbotsService = {
  getChatbots,
  getChatbot,
  getChatbotById,
  createChatbot,
  updateChatbot,
  deleteChatbot,
  copyChatbot,
  getTokenUsage,
  getWidgetScript,
  sendMessage,
  sendPersonalChatMessage,
  getPersonalChatHistory,
  getConversationsList,
  getChatbotConversations,
  getConversationMessages,
  getModulesCatalog,
  getChatbotModules,
  updateChatbotModules,
  getModulesOverview,
  createModuleCatalogItem,
  updateModuleCatalogItem,
  deleteModuleCatalogItem,
  getConversationTags,
  createConversationTag,
  updateConversationTag,
  deleteConversationTag,
  getConversationAnnotation,
  upsertConversationAnnotation,
  deleteConversationAnnotation,
  toggleConversationFavorite,
};

// Training/Knowledge management (admin)
export const getChatbotSettings = async (chatbotId: number): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/settings`);
  return data;
};

export const updateChatbotSettings = async (chatbotId: number, settings: any): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/settings`, settings);
  return data;
};

export const createKnowledgeSource = async (chatbotId: number, body: { type: string; title?: string; lang?: string; tags?: any; meta?: any; }): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources`, body);
  return data;
};

export const listKnowledgeSources = async (chatbotId: number): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources`);
  return data;
};

export const updateKnowledgeSource = async (
  chatbotId: number,
  sourceId: number,
  body: { title?: string; is_active?: boolean; tags?: any; lang?: string }
): Promise<any> => {
  const data = await apiClient.patch(`/ai-chatbots/${chatbotId}/sources/${sourceId}`, body);
  return data;
};

export const deleteKnowledgeSource = async (
  chatbotId: number,
  sourceId: number
): Promise<boolean> => {
  await apiClient.delete(`/ai-chatbots/${chatbotId}/sources/${sourceId}`);
  return true;
};

export const ingestTextToSource = async (chatbotId: number, sourceId: number, text: string): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/ingest-text`, { text });
  return data;
};

export const uploadSourceFile = async (chatbotId: number, sourceId: number, file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const ingestUploadedFile = async (chatbotId: number, sourceId: number, uploadId: number): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/ingest-upload/${uploadId}`);
  return data;
};

export const listSourceChunks = async (chatbotId: number, sourceId: number, params?: { skip?: number; limit?: number }): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/chunks`, { params });
  return data;
};

export const searchPreview = async (chatbotId: number, q: string): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/search/preview`, { params: { q } });
  return data;
};

export const getSourceContent = async (chatbotId: number, sourceId: number, version?: number): Promise<{ version: number; text: string; chunks: number }> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/content`, { params: { version } });
  return data;
};

export const listSourceJobs = async (chatbotId: number, sourceId: number, params?: { skip?: number; limit?: number }): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/jobs`, { params });
  return data;
};

export const getJobDetail = async (chatbotId: number, jobId: number): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/jobs/${jobId}`);
  return data;
};

export const clearChatbotCache = async (chatbotId: number): Promise<boolean> => {
  await apiClient.delete(`/ai-chatbots/${chatbotId}/cache`);
  return true;
};

export const generateSourceWithAi = async (chatbotId: number, formData: FormData): Promise<{ content: string; failed_sources?: string[]; debug_log?: string }> => {
  // Pass undefined as Content-Type content to let the browser set it with the boundary
  const data = await apiClient.post(`/ai-assistant/generate-source`, formData, {
    headers: { 'Content-Type': undefined } as any,
  });
  return data;
};

export default aiChatbotsService;